/**
 * Tasas personales de impuesto — viven en localStorage del navegador del user.
 *
 * Scope MVP: cada member pone sus propias tasas y ve su propio cálculo. Cuando
 * se agregue la fase corp (settings en D1 por Director), este helper se cambia
 * para que lea del API en vez de localStorage; el resto de la página no se toca.
 *
 * Las tasas se guardan como porcentaje 0..100 (no como fracción 0..1).
 */

const KEY = 'eve.tax.rates.v1'

export type TaxRates = {
  mining: number
  refining: number
  reactions: number
}

const DEFAULTS: TaxRates = { mining: 0, refining: 0, reactions: 0 }

export function loadTaxRates(): TaxRates {
  if (typeof localStorage === 'undefined') return DEFAULTS
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return DEFAULTS
    const parsed = JSON.parse(raw)
    return {
      mining: clamp(parsed.mining ?? 0),
      refining: clamp(parsed.refining ?? 0),
      reactions: clamp(parsed.reactions ?? 0),
    }
  } catch {
    return DEFAULTS
  }
}

export function saveTaxRates(rates: TaxRates): void {
  if (typeof localStorage === 'undefined') return
  const clean: TaxRates = {
    mining: clamp(rates.mining),
    refining: clamp(rates.refining),
    reactions: clamp(rates.reactions),
  }
  localStorage.setItem(KEY, JSON.stringify(clean))
}

export function taxUpdatedAt(): number | null {
  if (typeof localStorage === 'undefined') return null
  const raw = localStorage.getItem(KEY)
  if (!raw) return null
  // Re-parseo barato: si el formato cambia, devolvemos null.
  try {
    const parsed = JSON.parse(raw)
    return typeof parsed._updated_at === 'number' ? parsed._updated_at : null
  } catch {
    return null
  }
}

export function saveTaxRatesWithTimestamp(rates: TaxRates, ts: number): void {
  if (typeof localStorage === 'undefined') return
  const clean: TaxRates & { _updated_at: number } = {
    mining: clamp(rates.mining),
    refining: clamp(rates.refining),
    reactions: clamp(rates.reactions),
    _updated_at: ts,
  }
  localStorage.setItem(KEY, JSON.stringify(clean))
}

function clamp(n: number): number {
  if (!Number.isFinite(n)) return 0
  return Math.max(0, Math.min(100, n))
}

/** Exportada para que el UI reuse la misma semántica (no drift). */
export const clampTaxRate = clamp